/**
 * RetryRunAction — "Retry" for a gate-failed pre-deployment run.
 *
 * Click → dry-run preview in a modal: which targets re-run (staging unchanged
 * since the failure) and which are skipped and why (a newer commit superseded
 * them, or they are already queued / being evaluated). Confirm → the retryable
 * targets are queued and a sweep starts. The eval API re-checks everything on
 * both calls; this component only shows its verdict. Rendered by
 * PreDeploymentEvalTab only when canRetry(run).
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import Alert from "@amzn/awsui-components-react/polaris/alert";
import Badge from "@amzn/awsui-components-react/polaris/badge";
import Box from "@amzn/awsui-components-react/polaris/box";
import Button from "@amzn/awsui-components-react/polaris/button";
import Modal from "@amzn/awsui-components-react/polaris/modal";
import SpaceBetween from "@amzn/awsui-components-react/polaris/space-between";
import Table from "@amzn/awsui-components-react/polaris/table";
import { retryRun, RetryError } from "./preDeploymentRetryApi";
import type { RetryDecision, RetryPlanItem, RetryResult } from "./preDeploymentRetryApi";

const DECISION_COLOR: Record<RetryDecision, "blue" | "grey"> = {
  RETRY: "blue",
  SUPERSEDED: "grey",
  IN_FLIGHT: "grey",
};

type RetryNoticeKey =
  | "retry.nothing"
  | "retry.queuedNow"
  | "retry.queuedLater"
  | "retry.alreadyQueued"
  | "retry.failed";

/** A notice stays up until dismissed, so it keeps a key and is translated at render. */
export interface RetryNotice {
  type: "success" | "info" | "error";
  key: RetryNoticeKey;
  values?: { error: string };
}

/** What to tell the user after a non-dry-run call returned. */
export function retryNotice(result: RetryResult): RetryNotice {
  if (!result.queued) return { type: "info", key: "retry.nothing" };
  return { type: "success", key: result.sweepTriggered ? "retry.queuedNow" : "retry.queuedLater" };
}

/** The notice for a failed preview / queue call. */
export function retryErrorNotice(e: unknown): RetryNotice {
  if (e instanceof RetryError && e.reason === "ALREADY_QUEUED") {
    return { type: "error", key: "retry.alreadyQueued" };
  }
  return { type: "error", key: "retry.failed", values: { error: e instanceof Error ? e.message : String(e) } };
}

export default function RetryRunAction({ runId }: { runId: string }) {
  const { t } = useTranslation("eval");
  const columns = [
    {
      id: "target",
      header: t("retry.colTarget"),
      cell: (item: RetryPlanItem) => (item.agentName ? `${item.agentName}/${item.name}` : item.name),
    },
    { id: "category", header: t("retry.colCategory"), cell: (item: RetryPlanItem) => item.category },
    {
      id: "decision",
      header: t("retry.colDecision"),
      cell: (item: RetryPlanItem) => (
        <Badge color={DECISION_COLOR[item.decision]}>{t(`retry.decision.${item.decision}`)}</Badge>
      ),
    },
    { id: "reason", header: t("retry.colReason"), cell: (item: RetryPlanItem) => item.reason },
  ];
  const [plan, setPlan] = useState<RetryResult | null>(null);
  const [busy, setBusy] = useState<"preview" | "queue" | null>(null);
  const [notice, setNotice] = useState<RetryNotice | null>(null);

  const preview = async () => {
    setNotice(null);
    setBusy("preview");
    try {
      setPlan(await retryRun(runId, true));
    } catch (e) {
      setNotice(retryErrorNotice(e));
    } finally {
      setBusy(null);
    }
  };

  const queue = async () => {
    setBusy("queue");
    try {
      setNotice(retryNotice(await retryRun(runId, false)));
    } catch (e) {
      setNotice(retryErrorNotice(e));
    } finally {
      setBusy(null);
      setPlan(null);
    }
  };

  const retryable = plan?.items.some((item) => item.decision === "RETRY") ?? false;

  return (
    <SpaceBetween size="xs">
      <Box>
        <Button iconName="refresh" onClick={preview} loading={busy === "preview"}>
          {t("retry.retry")}
        </Button>
      </Box>
      {notice && (
        <Alert type={notice.type} dismissible onDismiss={() => setNotice(null)}>
          {t(notice.key, notice.values)}
        </Alert>
      )}
      <Modal
        visible={plan !== null}
        onDismiss={() => setPlan(null)}
        header={t("retry.title")}
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={() => setPlan(null)}>
                {t("retry.cancel")}
              </Button>
              <Button variant="primary" disabled={!retryable} loading={busy === "queue"} onClick={queue}>
                {t("retry.queue")}
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <SpaceBetween size="m">
          <Box color="text-body-secondary">
            {t("retry.body")}
          </Box>
          <Table variant="embedded" items={plan?.items ?? []} columnDefinitions={columns} />
        </SpaceBetween>
      </Modal>
    </SpaceBetween>
  );
}
